import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from "@angular/core";
import { ChartData, ChartType, ChartOptions, Chart } from "chart.js";
import { BaseChartDirective } from "ng2-charts";

@Component({
  selector: "app-doughnut-chart",
  templateUrl: "./doughnut-chart.component.html",
  styleUrls: ["./doughnut-chart.component.scss"]
})
export class DoughnutChartComponent implements OnInit, OnChanges {
  @Input() labels: string[] = [];
  @Input() data: number[] = [];
  @Input() isRefreshChart!: boolean;

  @ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;

  public doughnutChartType: ChartType = "doughnut";

  public doughnutChartOptions: ChartOptions | any = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "45%",
    plugins: {
      legend: {
        display: false // Disable default legend; custom legend will handle this
      }
    }
  };

  public doughnutChartData: ChartData<"doughnut"> = {
    labels: [],
    datasets: [{ data: [], backgroundColor: [] }]
  };

  baseColors: string[] = ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF", "#FF9F40"];

  // Pagination properties
  currentPage = 0;
  itemsPerPage = 12;
  hiddenLabels: Set<number> = new Set(); // Track hidden labels

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    this.doughnutChartData.labels = this.labels;
    this.doughnutChartData.datasets[0].data = this.data;

    const colorCount = this.data?.length;
    this.doughnutChartData.datasets[0].backgroundColor = this.generateColors(colorCount);

    this.doughnutChartData = { ...this.doughnutChartData };
  }

  ngOnInit(): void {}

  get maxPage() {
    return Math.ceil(this.labels.length / this.itemsPerPage) - 1;
  }

  get paginatedLabels() {
    const start = this.currentPage * this.itemsPerPage;
    const doughnutChartData: any = this.doughnutChartData.datasets[0];
    return this.labels?.map((label, index) => ({
      label,
      index: start + index, // Track the original index for toggling visibility
      color: doughnutChartData.backgroundColor[start + index] as string
    }));
  }

  previousPage() {
    if (this.currentPage > 0) {
      this.currentPage--;
    }
  }

  nextPage() {
    if (this.currentPage < this.maxPage) {
      this.currentPage++;
    }
  }

  toggleDataVisibility(index: number): void {
    if (this.chart?.chart) {
      const meta: any = this.chart.chart.getDatasetMeta(0);
      meta.data[index].hidden = !meta.data[index].hidden;
      if (meta.data[index].hidden) {
        this.hiddenLabels.add(index);
      } else {
        this.hiddenLabels.delete(index);
      }

      this.chart.update();
    }
  }

  generateColors(count: number): string[] {
    const colors: string[] = [];
    for (let i = 0; i < count; i++) {
      const baseColor = this.baseColors[i % this.baseColors.length];
      const variation = i / count;
      colors.push(this.adjustColorBrightness(baseColor, variation * 0.5));
    }
    return colors;
  }

  adjustColorBrightness(color: string, amount: number): string {
    let col = color.substring(1);
    let num = parseInt(col, 16);
    let r = (num >> 16) + amount * 255;
    let g = ((num >> 8) & 0x00ff) + amount * 255;
    let b = (num & 0x0000ff) + amount * 255;

    return (
      "#" +
      (
        0x1000000 +
        (Math.min(255, Math.max(0, Math.round(r))) << 16) +
        (Math.min(255, Math.max(0, Math.round(g))) << 8) +
        Math.min(255, Math.max(0, Math.round(b)))
      )
        .toString(16)
        .slice(1)
    );
  }
}
